import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSupabase } from "../SupabaseContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Layout, ArrowRight, Loader2, AlertCircle, Users } from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import { useToast } from "./ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import SpeechToTextInput from "./SpeechToTextInput";
import TextToSpeech from "./TextToSpeech";

const SituationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const supabase = useSupabase();
  const { toast } = useToast();
  const [situation, setSituation] = useState(null);
  const [solution, setSolution] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [responseStats, setResponseStats] = useState({
    totalUsers: 0,
    respondedUsers: 0,
  });

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) {
      navigate("/login");
    } else {
      setUser(storedUser);
      fetchSituationAndCheckSolution(storedUser.id);
    }
  }, [id, navigate]);

  const fetchSituationAndCheckSolution = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch situation details
      const { data: situationData, error: situationError } = await supabase
        .from("situations")
        .select("*")
        .eq("id", id)
        .single();

      if (situationError) {
        console.error("Error fetching situation:", situationError);
        throw new Error("Failed to load situation. Please try again.");
      }

      setSituation(situationData);

      // Check if user has already submitted a solution
      const { data: solutionData, error: solutionError } = await supabase
        .from("solutions")
        .select("*")
        .eq("situation_id", id)
        .eq("user_id", userId)
        .single();

      // PGRST116 is the "no rows returned" error code, which is expected if the user hasn't submitted a solution
      if (solutionError && solutionError.code !== "PGRST116") {
        console.error("Error checking solution:", solutionError);
        throw new Error(
          "Failed to check if you have already submitted a solution."
        );
      }

      // If user already has a solution, check if all group members responded before redirecting
      if (solutionData) {
        // Get user's facilities
        const { data: checkFacilities } = await supabase
          .from("user_facilities")
          .select("facility_id")
          .eq("user_id", userId)
          .eq("is_active", true);

        let allComplete = true;

        if (checkFacilities && checkFacilities.length > 0) {
          for (const uf of checkFacilities) {
            const { data: accessUsers } = await supabase
              .from("access_control")
              .select("user_id")
              .eq("situation_id", id)
              .eq("facility_id", uf.facility_id);

            if (!accessUsers || accessUsers.length === 0) continue;

            const { data: facSolutions } = await supabase
              .from("solutions")
              .select("user_id")
              .eq("situation_id", id)
              .in("user_id", accessUsers.map((u) => u.user_id));

            const uniqueResponded = new Set(facSolutions?.map((s) => s.user_id) || []);
            if (uniqueResponded.size < accessUsers.length) {
              allComplete = false;
              break;
            }
          }
        }

        if (allComplete) {
          navigate(`/evaluate-solutions/${id}`);
        } else {
          navigate("/situations");
        }
        return;
      }

      // Check if the user has access to this situation via access_control
      const { data: accessData, error: accessError } = await supabase
        .from("access_control")
        .select("facility_id")
        .eq("situation_id", id)
        .eq("user_id", userId);

      if (accessError) {
        console.error("Error checking access:", accessError);
        throw new Error("Failed to check access permissions.");
      }

      // If no access entries found, check user_facilities to get the user's facilities
      if (!accessData || accessData.length === 0) {
        console.log(
          "No direct access entries found, checking user's facilities..."
        );
      }

      // Get all facilities for this user
      const { data: userFacilities, error: facilitiesError } = await supabase
        .from("user_facilities")
        .select("facility_id")
        .eq("user_id", userId)
        .eq("is_active", true);

      if (facilitiesError) {
        console.error("Error fetching user facilities:", facilitiesError);
        throw new Error("Failed to load your facilities. Please try again.");
      }

      // Check if user has any facilities
      if (!userFacilities || userFacilities.length === 0) {
        console.error("User has no associated facilities");
        setError(
          "You don't have access to any facilities. Please contact an administrator."
        );
        setLoading(false);
        return;
      }

      // Fetch response statistics for all user's facilities
      const facilityIds = userFacilities.map((uf) => uf.facility_id);
      await fetchResponseStatsForFacilities(userId, facilityIds);
    } catch (error) {
      console.error("Error in fetchSituationAndCheckSolution:", error);
      setError(error.message || "Failed to load situation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Method to fetch stats across multiple facilities
  const fetchResponseStatsForFacilities = async (userId, facilityIds) => {
    try {
      let totalUsersAcrossFacilities = 0;
      let respondedUsersAcrossFacilities = 0;

      console.log("Fetching stats for facilities:", facilityIds);

      // For each facility, get users and their response status
      for (const facilityId of facilityIds) {
        // Get all users who have access to this situation from this facility
        const { data: accessData, error: accessError } = await supabase
          .from("access_control")
          .select("user_id, company_id")
          .eq("situation_id", id)
          .eq("facility_id", facilityId);

        if (accessError) {
          console.error("Error fetching access data:", accessError);
          continue;
        }

        if (!accessData || accessData.length === 0) {
          console.log(`No access entries found for facility ${facilityId}`);
          continue;
        }

        // Get user's company from any access record
        const userCompanyId = accessData.find(
          (access) => access.user_id === userId
        )?.company_id;

        if (!userCompanyId) {
          console.log("Could not determine user company ID");
          continue;
        }

        // Filter users from the same company
        const companyUsers = accessData.filter(
          (access) => access.company_id === userCompanyId
        );

        // Get all solutions for this situation from company users
        const { data: solutions, error: solutionsError } = await supabase
          .from("solutions")
          .select("user_id")
          .eq("situation_id", id)
          .in(
            "user_id",
            companyUsers.map((u) => u.user_id)
          );

        if (solutionsError) {
          console.error("Error fetching solutions:", solutionsError);
          continue;
        }

        totalUsersAcrossFacilities += companyUsers.length;
        const uniqueSolvers = new Set(solutions?.map((s) => s.user_id) || []);
        respondedUsersAcrossFacilities += uniqueSolvers.size;
      }

      console.log("Response stats:", {
        totalUsers: totalUsersAcrossFacilities,
        respondedUsers: respondedUsersAcrossFacilities,
      });

      setResponseStats({
        totalUsers: totalUsersAcrossFacilities,
        respondedUsers: respondedUsersAcrossFacilities,
      });
    } catch (error) {
      console.error("Error fetching response stats:", error);
    }
  };

  // Update the submit handler to consider all facilities
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // First submit the solution
      const { data, error } = await supabase.from("solutions").insert([
        {
          situation_id: id,
          user_id: user.id,
          content: solution,
        },
      ]);

      if (error) throw error;

      // Get all of the user's facilities
      const { data: userFacilities, error: facilitiesError } = await supabase
        .from("user_facilities")
        .select("facility_id")
        .eq("user_id", user.id)
        .eq("is_active", true);

      if (facilitiesError) throw facilitiesError;

      if (!userFacilities || userFacilities.length === 0) {
        throw new Error("You are not associated with any facilities");
      }

      // For each facility, check if all users have responded
      let allFacilitiesComplete = true;

      for (const userFacility of userFacilities) {
        // Get all users with access to this situation from this facility
        const { data: accessData, error: accessError } = await supabase
          .from("access_control")
          .select("user_id, facility_id")
          .eq("situation_id", id)
          .eq("facility_id", userFacility.facility_id);

        if (accessError) throw accessError;

        // Filter users from this facility
        const facilityUsers = accessData.filter(
          (access) => access.facility_id === userFacility.facility_id
        );

        if (facilityUsers.length === 0) continue;

        // Get all solutions for this situation from facility users
        const { data: solutions, error: solutionsError } = await supabase
          .from("solutions")
          .select("user_id")
          .eq("situation_id", id)
          .in(
            "user_id",
            facilityUsers.map((u) => u.user_id)
          );

        if (solutionsError) throw solutionsError;

        // If any facility is incomplete, the overall status is incomplete
        const uniqueRespondents = new Set(solutions?.map((s) => s.user_id) || []);
        if (uniqueRespondents.size < facilityUsers.length) {
          allFacilitiesComplete = false;
          break;
        }
      }

      if (allFacilitiesComplete) {
        // If all users in all facilities have responded, proceed to evaluations
        navigate(`/evaluate-solutions/${id}`);
      } else {
        // If some facilities are incomplete, show message and go to situations list
        toast({
          title: "Riešenie bolo úspešne odoslané",
          description:
            "Ďakujeme za vaše riešenie. Počkajte prosím, kým všetci používatelia odošlú svoje riešenia.",
          duration: 5000,
        });
        navigate("/situations");
      }
    } catch (error) {
      console.error("Error submitting solution:", error);
      setError("Failed to submit solution: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderHTML = (content) => {
    return { __html: content };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="max-w-md mx-auto mt-8">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <Card className="shadow-lg">
        <CardHeader className="text-center">
          <div className="mx-auto bg-red-100 text-red-600 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
            <Layout className="w-6 h-6" />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">
            {situation?.title}
          </CardTitle>
          <CardDescription className="text-lg text-gray-600 mt-2">
            Pozorne si prečítajte popis situácie a navrhnite vlastné riešenie.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center">
                <h3 className="font-semibold text-lg text-gray-700">
                  Popis situácie
                </h3>
                <TextToSpeech text={situation?.description || ""} />
              </div>
              <Card>
                <CardContent className="p-4 bg-gray-50">
                  <div
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={renderHTML(situation?.description)}
                  />
                </CardContent>
              </Card>
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex items-center">
                <h3 className="font-semibold text-lg text-gray-700 pr-2">
                  Vlastné riešenie situácie
                </h3>
                <SpeechToTextInput
                  onTranscriptionComplete={(text) => {
                    // Append the transcribed text to existing solution
                    setSolution((prev) => {
                      const prefix = prev ? prev + "\n\n" : "";
                      return prefix + text;
                    });

                    // Show success toast
                    toast({
                      title: "Audio transcribed successfully",
                      description:
                        "The transcribed text has been added to your solution.",
                      duration: 3000,
                    });
                  }}
                />
              </div>
              <Textarea
                placeholder="Popíšte vaše riešenie..."
                value={solution}
                onChange={(e) => setSolution(e.target.value)}
                rows={6}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              />
            </div>
          </form>
        </CardContent>
        <CardFooter className="flex justify-between items-center pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/situations")}
            className="px-4 py-2 text-red-600 border-red-600 hover:bg-red-50"
          >
            Späť na zoznam
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md transition duration-300 ease-in-out flex items-center"
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <>
                Odoslať riešenie
                <ArrowRight className="ml-2 w-4 h-4" />
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default SituationDetail;
